import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PAY_STATUSES = ["pending", "processing", "paid", "failed", "cancelled", "refunded", "manual_review"];
const STATUS_COLORS = { paid: "#00DC82", pending: "#9DA3B4", processing: "#FFD23F", failed: "#F87171", cancelled: "#6F7585", refunded: "#3AB4FF", manual_review: "#FF8A22" };

export default function AdminPayments() {
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [fStatus, setFStatus] = useState("all");
  const [fUser, setFUser] = useState("");

  const loadCfg = () => api.get("/admin/payments/config").then((r) => setCfg(r.data)).catch(() => {});
  const loadTx = () => api.get("/admin/payments", { params: { ...(fStatus !== "all" ? { status: fStatus } : {}), ...(fUser ? { user_q: fUser } : {}) } })
    .then((r) => { setItems(r.data.items); setTotals(r.data.totals); }).catch(() => {});
  useEffect(() => { loadCfg(); loadTx(); }, []);
  useEffect(() => { loadTx(); }, [fStatus]);

  const saveCfg = async (extra = {}) => {
    setSaving(true);
    try {
      const body = { enabled: cfg.enabled, env: cfg.env, channel: cfg.channel, website_url: cfg.website_url, payments_enabled: cfg.payments_enabled, ...extra };
      if (secret) body.secret = secret;
      const { data } = await api.put("/admin/payments/config", body);
      setCfg(data); setSecret("");
      toast.success("Payment settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const setTxStatus = async (payment_id, status) => {
    try { await api.patch(`/admin/payments/${payment_id}`, { status }); toast.success(`Marked ${status}`); loadTx(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!cfg) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/40" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1300px] mx-auto">
      <div className="kr-overline text-[#00DC82]">[ BILLING / PAYMENTS ]</div>
      <h1 className="font-display text-3xl mt-2 tracking-tight flex items-center gap-3"><DollarSign className="h-7 w-7 text-[#00DC82]" /> Payments</h1>

      {/* Whish config */}
      <div className="mt-6 kr-card p-5" data-testid="whish-config-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-lg">Whish Payment Provider</div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-white/70">
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="whish-enabled-switch" /> Whish enabled
            </label>
            <label className="flex items-center gap-2 text-sm text-white/70">
              <Switch checked={!!cfg.payments_enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, payments_enabled: v }))} data-testid="live-payments-switch" /> Live payments (off = Trial Mode)
            </label>
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <div className="kr-overline mb-1.5">ENVIRONMENT</div>
            <Select value={cfg.env} onValueChange={(v) => setCfg((c) => ({ ...c, env: v }))}>
              <SelectTrigger className="bg-[#07080B] border-white/10 h-10" data-testid="whish-env-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#101219] border-white/10">
                <SelectItem value="sandbox" className="focus:bg-white/5">Sandbox (test)</SelectItem>
                <SelectItem value="production" className="focus:bg-white/5">Production (live)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="kr-overline mb-1.5">CHANNEL ID</div>
            <Input value={cfg.channel} onChange={(e) => setCfg((c) => ({ ...c, channel: e.target.value }))} placeholder="merchant channel" className="bg-[#07080B] border-white/10 h-10" data-testid="whish-channel-input" />
          </div>
          <div>
            <div className="kr-overline mb-1.5">SECRET {cfg.has_secret && <span className="text-[#00DC82]">· SAVED</span>}</div>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={cfg.has_secret ? "•••••••• (leave blank to keep)" : "merchant secret"} className="bg-[#07080B] border-white/10 h-10" data-testid="whish-secret-input" />
          </div>
          <div>
            <div className="kr-overline mb-1.5">WEBSITE URL (REGISTERED)</div>
            <Input value={cfg.website_url} onChange={(e) => setCfg((c) => ({ ...c, website_url: e.target.value }))} placeholder="https://alphacore-ai.com" className="bg-[#07080B] border-white/10 h-10" data-testid="whish-website-input" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button onClick={() => saveCfg()} disabled={saving} className="h-10 text-black font-semibold border-0" style={{ background: "#00DC82" }} data-testid="whish-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
          </Button>
          <div className="text-xs text-white/40 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> The secret is write-only — it is never returned by the API after saving.</div>
        </div>
      </div>

      {/* Totals */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
        {PAY_STATUSES.map((s) => (
          <div key={s} className="kr-card p-3 text-center">
            <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: STATUS_COLORS[s] }}>{s.replace("_", " ")}</div>
            <div className="font-display text-lg mt-1">{totals[s]?.count || 0}</div>
            <div className="text-[10px] text-white/35">${(totals[s]?.amount || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      {/* Transactions */}
      <div className="mt-5 kr-card p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="bg-[#07080B] border-white/10 h-9 w-40 text-xs" data-testid="pay-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#101219] border-white/10">
              <SelectItem value="all" className="focus:bg-white/5">All statuses</SelectItem>
              {PAY_STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/5 capitalize">{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={fUser} onChange={(e) => setFUser(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadTx()} placeholder="Filter by user email…" className="bg-[#07080B] border-white/10 h-9 w-56 text-xs" data-testid="pay-filter-user" />
          <Button variant="outline" onClick={loadTx} className="h-9 text-xs border-white/10 hover:bg-white/5 hover:text-white">Apply</Button>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 border-b border-white/5">
                {["User", "Plan", "Amount", "Provider", "Txn ID", "Status", "Test", "Date", ""].map((h) => (
                  <th key={h} className="text-left p-2.5 font-mono text-[10px] tracking-wider uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.payment_id} className="border-b border-white/5" data-testid={`pay-row-${p.payment_id}`}>
                  <td className="p-2.5"><div>{p.user_email}</div><div className="text-[10px] text-white/35">{p.phone || ""}</div></td>
                  <td className="p-2.5 capitalize">{p.plan}</td>
                  <td className="p-2.5 font-mono">${p.amount} {p.currency}</td>
                  <td className="p-2.5 capitalize">{p.provider}</td>
                  <td className="p-2.5 font-mono text-xs text-white/45">{p.provider_txn_id || p.external_id}</td>
                  <td className="p-2.5"><span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border" style={{ color: STATUS_COLORS[p.status], borderColor: `${STATUS_COLORS[p.status]}55` }}>{p.status.replace("_", " ")}</span>
                    {p.error && <div className="text-[10px] text-red-300 mt-1 max-w-[160px] truncate">{p.error}</div>}
                  </td>
                  <td className="p-2.5 text-xs">{p.test ? "TEST" : "LIVE"}</td>
                  <td className="p-2.5 text-xs text-white/45">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="p-2.5">
                    {!["refunded", "manual_review"].includes(p.status) && (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setTxStatus(p.payment_id, "manual_review")} className="h-7 text-[10px] border-white/10 hover:bg-white/5 hover:text-white" data-testid={`pay-review-${p.payment_id}`}>Review</Button>
                        {p.status === "paid" && <Button size="sm" variant="outline" onClick={() => setTxStatus(p.payment_id, "refunded")} className="h-7 text-[10px] border-blue-400/25 text-blue-300 hover:bg-blue-500/10" data-testid={`pay-refund-${p.payment_id}`}>Refund</Button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-white/35">No payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
